import {
  CSSProperties,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { useMediaQuery } from "@material-ui/core";
import Axios from "axios";
import { Helmet } from "react-helmet";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { AppContext } from "contexts";
import * as routes from "routes";
import {
  getPageURL,
  getRouteInfoFromRouteName,
  routeEquals,
} from "routes/config";
import { getMenuItems } from "services/app";
import { MenuItems } from "services/app/types";
import theme from "theme";
import { desktopHeaderHeight, mobileHeaderHeight } from "theme/constants";

import { SITE_TITLE } from "../../constants";
import Footer from "./components/Footer";
import Header from "./components/Header";
import Notifications from "./components/Notifications";

interface LayoutProps extends RouteComponentProps {
  children?: ReactNode;
}

const Layout: FC<LayoutProps> = ({ location, children }) => {
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });
  const [menuItems, setMenuItems] = useState<MenuItems>(null);
  const [navPadding, setNavPadding] = useState<number>(0);
  const [containerStyle, setContainerStyle] = useState<CSSProperties>({});
  const [canonicalUrl, setCanonicalURL] = useState<string>("");
  const [width, setWidth] = useState(window.innerWidth);

  const { domainNames } = useContext(AppContext);

  const currentRoute = location.pathname;
  const routeInfo = getRouteInfoFromRouteName(currentRoute);
  const sellerSide = routeInfo.domain === "worth";
  const displayOrderBookContainer =
    routeEquals(routes.directoryBuyerDashboard(), currentRoute) ||
    routeEquals(routes.overview(), currentRoute) ||
    routeEquals(routes.sellerNotFound(), currentRoute) ||
    routeEquals(routes.orderBookPay(), currentRoute);

  const cancelToken = Axios.CancelToken.source().token;
  useEffect(() => {
    let current = true;
    const loadMenuItems = async () => {
      try {
        const menuItems = await getMenuItems(cancelToken);
        if (current) {
          setMenuItems(menuItems);
        }
      } catch (e) {
        if (cancelToken) {
          return;
        }
        if (current) {
          setMenuItems(null);
        }
      }
    };
    if (!menuItems) {
      loadMenuItems();
    }
    return () => {
      current = false;
    };
  }, [cancelToken, menuItems]);

  useEffect(() => {
    const updateWidth = () => setWidth(window.innerWidth);
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  useEffect(() => {
    if (routeInfo.config.showNav) {
      setNavPadding(isMdUp ? desktopHeaderHeight : mobileHeaderHeight);
    } else if (navPadding !== 0) {
      setNavPadding(0);
    }
  }, [routeInfo.config.showNav, isMdUp, width, location.pathname, navPadding]);

  useEffect(() => {
    const backgroundColor = routeInfo.config.background || "";
    const paddingTop = `${navPadding}px`;

    setContainerStyle({
      backgroundColor,
      paddingTop,
    });
  }, [routeInfo.config.background, navPadding]);

  useEffect(() => {
    setCanonicalURL(getPageURL(currentRoute, domainNames));
  }, [currentRoute, domainNames]);

  return (
    <>
      <Helmet
        title={`${routeInfo.config.buildTitle(
          location.pathname,
        )} | ${SITE_TITLE}`}
        meta={[
          {
            property: "og:url",
            content: canonicalUrl,
          },
          {
            name: "twitter:url",
            content: canonicalUrl,
          },
          {
            property: "canonical",
            content: canonicalUrl,
          },
        ].filter(Boolean)}
      />
      <div className={routeInfo.config.bodyClass} style={containerStyle}>
        {menuItems && routeInfo.config.showNav && (
          <Header
            menuItems={menuItems}
            sellerSide={sellerSide}
            isListings={displayOrderBookContainer}
          />
        )}
        <Notifications />
        <div
          id="react-root"
          className={
            !displayOrderBookContainer
              ? "react-base-height orderbook-container"
              : ""
          }
        >
          {children}
        </div>
        {menuItems && routeInfo.config.showFooter && (
          <Footer menuItems={menuItems} />
        )}
      </div>
    </>
  );
};

export default withRouter(Layout);
